import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useI18n } from "@/i18n/I18nContext";
import { DirectionProvider } from "@radix-ui/react-direction";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import "@/App.css";

import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import Patients from "@/pages/Patients";
import Facilities from "@/pages/Facilities";
import Roles from "@/pages/Roles";
import Lookups from "@/pages/Lookups";
import ServiceCatalog from "@/pages/ServiceCatalog";
import BrandingSettings from "@/pages/admin/BrandingSettings";
import PresentLayout from "@/components/layout/PresentLayout";
import DrugFormulary from "@/pages/DrugFormulary";
import TestCatalog from "@/pages/TestCatalog";
import Audit from "@/pages/Audit";
import Protocols from "@/pages/Protocols";
import Reports from "@/pages/Reports";
import SmsConsole from "@/pages/SmsConsole";

import AccessControl from "@/pages/AccessControl";
import CarePlanQueue from "@/pages/careplan/CarePlanQueue";
import CarePlanEditor from "@/pages/careplan/CarePlanEditor";
import CarePlanVersions from "@/pages/careplan/CarePlanVersions";
import FormLibrary from "@/pages/forms/FormLibrary";
import FormDesigner from "@/pages/forms/FormDesigner";
import MobileAppForms from "@/pages/forms/MobileAppForms";
import MobileAppPageEditor from "@/pages/forms/MobileAppPageEditor";

import OnboardingDashboard from "@/pages/onboarding/OnboardingDashboard";
import RequestList from "@/pages/onboarding/RequestList";
import CreateRequest from "@/pages/onboarding/CreateRequest";
import RequestDetail from "@/pages/onboarding/RequestDetail";

import HotlineDashboard from "@/pages/hotline/HotlineDashboard";
import HotlineSessions from "@/pages/hotline/HotlineSessions";
import NewHotlineSession from "@/pages/hotline/NewHotlineSession";
import HotlineSessionDetail from "@/pages/hotline/HotlineSessionDetail";

import RpmDashboard from "@/pages/rpm/RpmDashboard";
import RpmPatientDetail from "@/pages/rpm/RpmPatientDetail";
import RpmAlerts from "@/pages/rpm/RpmAlerts";
import RpmInterventions from "@/pages/rpm/RpmInterventions";
import RpmThresholds from "@/pages/rpm/RpmThresholds";

import FleetDashboard from "@/pages/fleet/FleetDashboard";
import FleetMap from "@/pages/fleet/FleetMap";
import FleetVisits from "@/pages/fleet/FleetVisits";
import MyVisits from "@/pages/fleet/MyVisits";
import MyRoute from "@/pages/fleet/MyRoute";
import Orchestrator from "@/pages/fleet/Orchestrator";
import LocationWorklist from "@/pages/fleet/LocationWorklist";
import FleetClinicians from "@/pages/fleet/FleetClinicians";
import AvailabilityBoard from "@/pages/fleet/AvailabilityBoard";
import FleetVehicles from "@/pages/fleet/FleetVehicles";

import { TeleconsultDashboard, TeleconsultRoom } from "@/pages/Teleconsult";

import ClinicalDashboard from "@/pages/clinical/ClinicalDashboard";
import ClinicalTasks from "@/pages/clinical/ClinicalTasks";
import ClinicalPatientProfile from "@/pages/clinical/ClinicalPatientProfile";
import RemindersAdherence from "@/pages/clinical/RemindersAdherence";
import SosLogDashboard from "@/pages/critical/SosLogDashboard";
import MyProfile from "@/pages/profile/MyProfile";
import SlotViewer from "@/pages/availability/SlotViewer";
import Support from "@/pages/Support";
import MyCallbacks from "@/pages/callbacks/MyCallbacks";
import EscalatedCallbacks from "@/pages/callbacks/EscalatedCallbacks";
import CallbackSlaSettings from "@/pages/callbacks/CallbackSlaSettings";
import CallbackQuestionnaire from "@/pages/callbacks/CallbackQuestionnaire";
import FaqPage from "@/pages/help/Faq";
import FaqAdmin from "@/pages/admin/FaqAdmin";

import {
  ExecutiveDashboard, OperationalDashboard, ClinicalSafetyDashboard,
  PopulationHealthDashboard, InterventionsDashboard, SlaManager,
  PatientExplorer,
} from "@/pages/command/CommandCenter";

function Protected({ children, adminOnly, denyRoles, allowRoles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B7280] text-sm" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role_code !== "admin") return <Navigate to="/onboarding" replace />;
  if (denyRoles && denyRoles.includes(user.role_code)) return <Navigate to="/" replace />;
  if (allowRoles && !allowRoles.includes(user.role_code)) return <Navigate to="/" replace />;
  return children;
}

function RoleHome() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role_code === "admin") return <Dashboard />;
  if (user.role_code === "hah_nurse") return <Navigate to="/hotline" replace />;
  if (["physician", "hah_physician", "nurse", "care_coordinator", "hah_coordinator", "clinical_director"].includes(user.role_code))
    return <Navigate to="/clinical" replace />;
  return <Navigate to="/onboarding" replace />;
}

export default function App() {
  const { isRTL } = useI18n();
  return (
    <DirectionProvider dir={isRTL ? "rtl" : "ltr"}>
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors closeButton position={isRTL ? "top-left" : "top-right"} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/help" element={<Support variant="public" />} />
          <Route
            path="/"
            element={
              <Protected>
                <AppLayout />
              </Protected>
            }
          >
            <Route index element={<RoleHome />} />
            <Route path="profile" element={<MyProfile />} />
            <Route path="support" element={<Support variant="app" />} />
            <Route path="availability" element={<SlotViewer />} />
            <Route path="users" element={<Protected adminOnly><Users /></Protected>} />
            <Route path="patients" element={<Protected adminOnly><Patients /></Protected>} />
            <Route path="facilities" element={<Protected adminOnly><Facilities /></Protected>} />
            <Route path="roles" element={<Protected adminOnly><Roles /></Protected>} />
            <Route path="access-control" element={<Protected adminOnly><AccessControl /></Protected>} />
            <Route path="lookups" element={<Protected adminOnly><Lookups /></Protected>} />
            <Route path="service-catalog" element={<Protected adminOnly><ServiceCatalog /></Protected>} />
            <Route path="drug-formulary" element={<Protected adminOnly><DrugFormulary /></Protected>} />
            <Route path="test-catalog" element={<Protected adminOnly><TestCatalog /></Protected>} />
            <Route path="audit" element={<Protected adminOnly><Audit /></Protected>} />
            <Route path="branding" element={<Protected adminOnly><BrandingSettings /></Protected>} />
            <Route path="protocols" element={<Protected adminOnly><Protocols /></Protected>} />
            <Route path="reports" element={<Reports />} />
            <Route path="sms" element={<Protected adminOnly><SmsConsole /></Protected>} />
            <Route path="care-plans" element={<CarePlanQueue />} />
            <Route path="care-plans/:id" element={<CarePlanEditor />} />
            <Route path="care-plans/:id/versions" element={<CarePlanVersions />} />
            <Route path="forms" element={<FormLibrary />} />
            <Route path="forms/mobile-app" element={<MobileAppForms />} />
            <Route path="forms/mobile-app/:id" element={<MobileAppPageEditor />} />
            <Route path="forms/:id" element={<FormDesigner />} />

            <Route path="onboarding" element={<OnboardingDashboard />} />
            <Route path="onboarding/new" element={<CreateRequest mode="new" />} />
            <Route path="onboarding/requests" element={<RequestList />} />
            <Route path="onboarding/requests/:id" element={<RequestDetail />} />
            <Route path="onboarding/requests/:id/edit" element={<CreateRequest mode="edit" />} />

            <Route path="hotline" element={<HotlineDashboard />} />
            <Route path="hotline/new" element={<NewHotlineSession />} />
            <Route path="hotline/sessions" element={<HotlineSessions />} />
            <Route path="hotline/sessions/:id" element={<HotlineSessionDetail />} />

            <Route path="rpm" element={<RpmDashboard />} />
            <Route path="rpm/patients/:id" element={<RpmPatientDetail />} />
            <Route path="rpm/alerts" element={<RpmAlerts />} />
            <Route path="rpm/interventions" element={<RpmInterventions />} />
            <Route path="rpm/thresholds" element={<Protected adminOnly><RpmThresholds /></Protected>} />

            <Route path="fleet" element={<FleetDashboard />} />
            <Route path="fleet/orchestrate" element={<Protected denyRoles={["physician", "hah_physician", "home_care_team"]}><Orchestrator /></Protected>} />
            <Route path="fleet/location-worklist" element={<Protected denyRoles={["physician", "hah_physician", "home_care_team"]}><LocationWorklist /></Protected>} />
            <Route path="fleet/map" element={<Protected denyRoles={["home_care_team"]}><FleetMap /></Protected>} />
            <Route path="fleet/visits" element={<FleetVisits />} />
            <Route path="my-visits" element={<Protected><MyVisits /></Protected>} />
            <Route path="my-route" element={<Protected><MyRoute /></Protected>} />
            <Route path="fleet/clinicians" element={<FleetClinicians />} />
            <Route path="fleet/availability-board" element={<AvailabilityBoard />} />
            <Route path="fleet/vehicles" element={<Protected adminOnly><FleetVehicles /></Protected>} />

            <Route path="teleconsult" element={<TeleconsultDashboard />} />
            <Route path="teleconsult/room/:id" element={<TeleconsultRoom />} />

            <Route path="clinical" element={<ClinicalDashboard mode="active" />} />
            <Route path="clinical/my-patients" element={<ClinicalDashboard mode="mine" />} />
            <Route path="clinical/high-risk" element={<ClinicalDashboard mode="high_risk" />} />
            <Route path="clinical/tasks" element={<ClinicalTasks />} />
            <Route path="clinical/reminders" element={<RemindersAdherence />} />
            <Route path="clinical/patients/:id" element={<ClinicalPatientProfile />} />
            <Route path="critical/sos-log" element={<SosLogDashboard />} />

            <Route path="callbacks/mine" element={<MyCallbacks />} />
            <Route path="callbacks/escalated" element={<Protected denyRoles={["home_care_team", "hah_technician", "hah_physiotherapist", "physician", "hah_physician"]}><EscalatedCallbacks /></Protected>} />
            <Route path="callbacks/sla-settings" element={<Protected adminOnly><CallbackSlaSettings /></Protected>} />
            <Route path="callbacks/questionnaire" element={<Protected allowRoles={["admin", "hah_coordinator", "care_coordinator"]}><CallbackQuestionnaire /></Protected>} />
            <Route path="help/faq" element={<Protected><FaqPage /></Protected>} />
            <Route path="admin/faq" element={<Protected adminOnly><FaqAdmin /></Protected>} />

            <Route path="command" element={<Protected denyRoles={["physician", "hah_physician"]}><ExecutiveDashboard /></Protected>} />
            <Route path="command/operational" element={<Protected denyRoles={["physician", "hah_physician"]}><OperationalDashboard /></Protected>} />
            <Route path="command/clinical-safety" element={<Protected denyRoles={["physician", "hah_physician"]}><ClinicalSafetyDashboard /></Protected>} />
            <Route path="command/population" element={<Protected denyRoles={["physician", "hah_physician"]}><PopulationHealthDashboard /></Protected>} />
            <Route path="command/interventions" element={<Protected denyRoles={["physician", "hah_physician"]}><InterventionsDashboard /></Protected>} />
            <Route path="command/sla" element={<Protected denyRoles={["physician", "hah_physician"]}><SlaManager /></Protected>} />
            <Route path="command/patient-explorer" element={<Protected denyRoles={["physician", "hah_physician"]}><PatientExplorer /></Protected>} />
            <Route path="command/reports" element={<Navigate to="/reports" replace />} />
          </Route>
          <Route path="/present" element={<Protected denyRoles={["physician", "hah_physician"]}><PresentLayout /></Protected>}>
            <Route path="command" element={<ExecutiveDashboard />} />
            <Route path="command/operational" element={<OperationalDashboard />} />
            <Route path="command/clinical-safety" element={<ClinicalSafetyDashboard />} />
            <Route path="command/population" element={<PopulationHealthDashboard />} />
            <Route path="command/interventions" element={<InterventionsDashboard />} />
            <Route path="command/sla" element={<SlaManager />} />
            <Route path="command/patient-explorer" element={<PatientExplorer />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </DirectionProvider>
  );
}
